/**
 * Lightweight, privacy-friendly analytics client.
 *
 * - Respects Do-Not-Track: no events are sent if navigator.doNotTrack === '1'.
 * - Sends to our backend at ${API}/analytics/event by default.
 * - Third-party providers (GA4/Plausible) can be layered later via `config/site.js`.
 */
import axios from 'axios';
import { API } from '@/lib/api';
import { ANALYTICS_CONFIG } from '@/config/site';

const SESSION_KEY = 'aich_session_id';
const REF_KEY = 'smi_ref';
const REF_TTL_MS = 90 * 24 * 3600 * 1000; // 90-day attribution window

export function captureReferral() {
    try {
        const code = new URLSearchParams(window.location.search).get('ref');
        if (code && /^[a-z0-9][a-z0-9-]{2,39}$/i.test(code)) {
            localStorage.setItem(REF_KEY, JSON.stringify({ code, ts: Date.now() }));
        }
    } catch { /* storage unavailable */ }
}

export function getReferralCode() {
    try {
        const raw = localStorage.getItem(REF_KEY);
        if (!raw) return '';
        const { code, ts } = JSON.parse(raw);
        if (!code || Date.now() - ts > REF_TTL_MS) {
            localStorage.removeItem(REF_KEY);
            return '';
        }
        return code;
    } catch {
        return '';
    }
}

function isDNT() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    return (
        navigator.doNotTrack === '1' ||
        window.doNotTrack === '1' ||
        navigator.msDoNotTrack === '1'
    );
}

function getSessionId() {
    try {
        let id = localStorage.getItem(SESSION_KEY);
        if (!id) {
            id = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return null;
    }
}

export function trackEvent(eventType, properties = {}) {
    if (isDNT()) return;
    if (!ANALYTICS_CONFIG.backendEnabled) return;
    const ref = getReferralCode();
    const payload = {
        event_type: eventType,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        properties: ref ? { ...properties, ref } : properties,
        session_id: getSessionId(),
    };
    // Fire-and-forget. Never throw from analytics.
    axios
        .post(`${API}/analytics/event`, payload, {
            headers: { 'Content-Type': 'application/json' },
        })
        .catch(() => {});
}

export function trackPageView(path) {
    trackEvent('page_view', { path });
}
